import { Scene } from '../core/entity.js';
import { Tilemap } from '../core/tilemap.js';
import { TILE_DEFS } from '../render/tiles_art.js';
import { createPlayer } from '../entities/player.js';
import { getProp } from '../data/items.js';
import { canStand } from '../systems/movement.js';
import { Repo } from '../data/schema.js';
import { movementSystem } from '../systems/movement.js';
import { poseSystem } from '../systems/pose.js';
import { interactionSystem } from '../systems/interaction.js';
import { timeSystem } from '../systems/time.js';
import { persistSystem } from '../systems/persist.js';
import { vehicleSystem } from '../systems/vehicle.js';
import { cultivateSystem } from '../systems/cultivate.js';

/** 场景配置：地图是「生成」而不是手写字符画，改参数即可改户型 */
export const HOME_CFG = {
  W: 30, H: 20, TS: 40,
  innerWallRow: 9,      // 分隔室内与庭院的墙
  doors: [13, 14],      // 门所在列
  plots: { x0: 3, y0: 11, x1: 7, y1: 13 },  // 菜地土块
  path: { x0: 12, y0: 11, x1: 15, y1: 18 }, // 石板路
  spawn: { gx: 14, gy: 7 },
};

export const TILE_CHARS = {
  '#': 'wall',
  '.': 'wood',
  ',': 'grass',
  ':': 'soil',
  '~': 'stone',
  '+': 'door',
  ' ': 'void',
};

export function generateHomeMap(cfg = HOME_CFG) {
  const { W, H, innerWallRow, doors, plots, path } = cfg;
  const rows = [];
  for (let y = 0; y < H; y++) {
    let s = '';
    for (let x = 0; x < W; x++) {
      let c;
      if (x === 0 || y === 0 || x === W - 1 || y === H - 1) c = '#';
      else if (y === innerWallRow) c = doors.includes(x) ? '+' : '#';
      else if (y < innerWallRow) c = '.';                 // 室内木地板
      else if (x >= plots.x0 && x <= plots.x1 && y >= plots.y0 && y <= plots.y1) c = ':';
      else if (x >= path.x0 && x <= path.x1 && y >= path.y0 && y <= path.y1) c = '~';
      else c = ',';                                        // 庭院草地
      s += c;
    }
    rows.push(s);
  }
  return rows;
}

/** 开局默认陈设（破败版，之后靠玩家添置） */
export const DEFAULT_PROPS = [
  // 室内
  { type: 'bed', gx: 3, gy: 2 },
  { type: 'rug', gx: 12, gy: 3 },
  { type: 'cushion', gx: 13, gy: 4 },
  { type: 'shelf', gx: 7, gy: 1 },
  { type: 'desk', gx: 10, gy: 1 },
  { type: 'stool', gx: 10, gy: 2 },
  { type: 'stove', gx: 22, gy: 2 },
  { type: 'rice_bin', gx: 23, gy: 2 },   // 米缸：灶上缺米来这儿舂、来这儿舀
  { type: 'jar', gx: 25, gy: 2 },
  { type: 'chest', gx: 3, gy: 6 },
  { type: 'lamp', gx: 19, gy: 7 },
  { type: 'tv', gx: 17, gy: 2 },
  { type: 'whiteboard', gx: 18, gy: 2 },   // 立在电视右手边
  { type: 'gametable', gx: 24, gy: 5 },
  // 庭院
  { type: 'sign', gx: 15, gy: 10 },
  // 院门：门外就是车棚；车棚那头「回屋」落回来也正是这一格
  { type: 'portal', gx: 14, gy: 18, state: { to: 'carport', spawn: [6, 2], label: '车棚' } },
  { type: 'plot', gx: 3, gy: 11 },
  { type: 'plot', gx: 4, gy: 11 },
  { type: 'plot', gx: 5, gy: 11 },
  { type: 'paddy_plot', gx: 6, gy: 11 },   // 水田：插秧割谷，米的来路
  { type: 'stone_lantern', gx: 18, gy: 11 },
  { type: 'well', gx: 20, gy: 14 },
  { type: 'tree', gx: 24, gy: 12 },
  { type: 'tree', gx: 5, gy: 16 },
  { type: 'fence', gx: 9, gy: 17 },
  { type: 'fence', gx: 10, gy: 17 },
];

/** 把数据库中的器物行还原成场景里的 prop 对象 */
export function loadProps(db, scene) {
  const table = db.table('props');
  let rows = table.whereEq('scene', scene.id);
  if (!rows.length) {
    for (const def of DEFAULT_PROPS) table.insert({ scene: scene.id, ...def });
    rows = table.whereEq('scene', scene.id);
  } else {
    // 老存档补齐后来新增的默认陈设（按 type 判断，玩家已挪动的不动）
    const has = new Set(rows.map((r) => r.type));
    let added = false;
    for (const def of DEFAULT_PROPS) {
      if (has.has(def.type)) continue;
      table.insert({ scene: scene.id, ...def });
      has.add(def.type);
      added = true;
    }
    if (added) rows = table.whereEq('scene', scene.id);
  }
  scene.props = rows.map((r) => {
    const def = getProp(r.type);
    return {
      id: r.id,
      type: r.type,
      gx: r.gx, gy: r.gy,
      // 尺寸一律以器物定义为准：props 表给 w/h 兜了 1 的默认值，
      // 直接信表的话，床、地毯、游戏桌这类占多格的家具会被压成一格
      w: def.w || r.w || 1,
      h: def.h || r.h || 1,
      solid: !!def.solid,
      // 器物自身定义的默认状态 + 存档里的状态
      state: { ...(def.state || {}), ...(r.state || {}) },
      def,
    };
  });
}

/** 落点被占（或压根没有存档位置）就退回门口，门口也被占就近找空地 */
function findStandSpot(scene, x, y, tm) {
  if (x && y && canStand(scene, x, y)) return { x, y };
  if (canStand(scene, tm.spawn.x, tm.spawn.y)) return { x: tm.spawn.x, y: tm.spawn.y };
  const ts = tm.tileSize;
  for (let r = 1; r <= 8; r += 1) {
    for (const [ox, oy] of [[r, 0], [-r, 0], [0, r], [0, -r], [r, r], [-r, r], [r, -r], [-r, -r]]) {
      const nx = tm.spawn.x + ox * ts;
      const ny = tm.spawn.y + oy * ts;
      if (canStand(scene, nx, ny)) return { x: nx, y: ny };
    }
  }
  return { x: tm.spawn.x, y: tm.spawn.y };
}

export function createHomeScene(engine) {
  const scene = new Scene({ id: 'home', name: '洞天居' });
  const cfg = HOME_CFG;
  const tm = new Tilemap({
    rows: generateHomeMap(cfg),
    chars: TILE_CHARS,
    tiles: TILE_DEFS,
    tileSize: cfg.TS,
  });
  tm.spawn = { x: cfg.spawn.gx * cfg.TS + cfg.TS / 2, y: cfg.spawn.gy * cfg.TS + cfg.TS / 2 };
  scene.setTilemap(tm);

  scene.build = (eng) => {
    const db = eng.db;
    loadProps(db, scene);

    const pdata = Repo.getPlayer(db);
    // 上回存档的位置可能已被家具占住（布置时挪到脚底下），那人一进门就走不动了，先找个站得住的地方
    const spot = findStandSpot(scene, pdata.x, pdata.y, tm);
    const px = spot.x, py = spot.y;
    const player = createPlayer({ x: px, y: py, facing: pdata.facing || 'down' });
    scene.add(player);
    scene.player = player;

    eng.camera.setBounds(tm.pixelW, tm.pixelH);
    eng.camera.follow(player);
    eng.camera.snapTo(px, py);

    scene
      .use(timeSystem)
      .use(poseSystem)          // 姿态先于移动：起身那一帧就能迈步
      .use(movementSystem)
      .use(interactionSystem)
      .use(cultivateSystem)     // 吐纳：坐在蒲团 / 香炉上时灵气自涨，跟着 pose 走
      .use(vehicleSystem)        // 车：有自己的转向与碰撞，放在移动之后接管
      .use(persistSystem);
  };

  return scene;
}
